import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

interface DropdownItem {
  label: ReactNode;
  onSelect?: () => void;
  href?: string;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  /** Separator render eder (öteki alanlar yok sayılır). */
  separator?: boolean;
}

interface DropdownMenuProps {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: 'start' | 'end';
  className?: string;
  /** Genişlik (varsayılan: auto). */
  menuWidth?: string;
}

/**
 * Headless dropdown menu — basit, klavye-aware (arrow nav, Enter, Esc).
 * Portal kullanmaz (parent stacking yeterli).
 */
export function DropdownMenu({
  trigger,
  items,
  align = 'end',
  className,
  menuWidth = 'min-w-[12rem]',
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | HTMLAnchorElement | null>>([]);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open && activeIndex >= 0) {
      itemRefs.current[activeIndex]?.focus();
    }
  }, [open, activeIndex]);

  const selectableIndices = items.map((item, i) => (item.separator || item.disabled ? -1 : i)).filter((i) => i >= 0);

  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = selectableIndices.find((i) => i > activeIndex) ?? selectableIndices[0];
      setActiveIndex(next);
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const reversed = [...selectableIndices].reverse();
      const prev = reversed.find((i) => i < activeIndex) ?? reversed[0];
      setActiveIndex(prev);
    }
  }

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)} onKeyDown={onKey}>
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) setActiveIndex(selectableIndices[0] ?? -1);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink"
      >
        {trigger}
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute z-50 mt-1 rounded-custel-md border border-custel-border bg-custel-surface py-1 shadow-custel-lg',
            align === 'end' ? 'right-0' : 'left-0',
            menuWidth,
          )}
        >
          {items.map((item, i) => {
            if (item.separator) {
              return <div key={i} className="my-1 border-t border-custel-border" role="separator" />;
            }
            const cls = cn(
              'flex w-full items-center gap-2 px-3 py-1.5 text-sm transition-colors focus:outline-none',
              item.disabled
                ? 'cursor-not-allowed text-custel-text-tertiary'
                : item.destructive
                  ? 'text-custel-danger hover:bg-custel-danger-subtle focus:bg-custel-danger-subtle'
                  : 'text-custel-text hover:bg-custel-bg-subtle focus:bg-custel-bg-subtle',
            );
            const handle = () => {
              if (item.disabled) return;
              item.onSelect?.();
              setOpen(false);
            };
            if (item.href) {
              return (
                <a
                  key={i}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  href={item.href}
                  className={cls}
                  role="menuitem"
                  tabIndex={activeIndex === i ? 0 : -1}
                  onClick={handle}
                >
                  {item.icon && <span className="shrink-0">{item.icon}</span>}
                  <span className="flex-1 truncate text-left">{item.label}</span>
                </a>
              );
            }
            return (
              <button
                key={i}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                className={cls}
                role="menuitem"
                tabIndex={activeIndex === i ? 0 : -1}
                disabled={item.disabled}
                onClick={handle}
              >
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                <span className="flex-1 truncate text-left">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
